import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const SERVICES = ["Commercial Buildings", "Civil Construction", "Renovation", "Interiors & Fit-Out", "Infrastructure"];

const CONTACT = {
  phone: "07831 522491",
  phoneRaw: "07831522491",
  email: "thanish@reddyconstructionsltd.com",
  address: "19 North High Street, London E6 1HS",
};

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-line focus:border-flame focus:outline-none py-4 text-lg placeholder:text-secondary-foreground/40 transition-colors";

  return (
    <section id="contact" className="bg-ink py-24 md:py-36" data-testid="contact">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-flame" />
            <span className="text-xs uppercase tracking-[0.35em] text-secondary-foreground/80">Get in touch</span>
          </div>
          <h2 className="font-heading font-black tracking-tighter text-4xl md:text-6xl leading-[0.95] mb-10">
            Let's build <span className="text-flame">something.</span>
          </h2>

          <div className="space-y-6">
            <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-start gap-4 group" data-testid="contact-phone">
              <Phone className="h-5 w-5 text-flame mt-1 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-widest text-secondary-foreground/60">Phone</p>
                <p className="text-lg group-hover:text-flame transition-colors">{CONTACT.phone}</p>
              </div>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-start gap-4 group" data-testid="contact-email">
              <Mail className="h-5 w-5 text-flame mt-1 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-widest text-secondary-foreground/60">Email</p>
                <p className="text-lg group-hover:text-flame transition-colors break-all">{CONTACT.email}</p>
              </div>
            </a>
            <div className="flex items-start gap-4" data-testid="contact-address">
              <MapPin className="h-5 w-5 text-flame mt-1 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-widest text-secondary-foreground/60">Office</p>
                <p className="text-lg">{CONTACT.address}</p>
              </div>
            </div>
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          onSubmit={submit}
          className="lg:col-span-7 space-y-8"
          data-testid="inquiry-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <input className={inputCls} placeholder="Your name *" value={form.name} onChange={update("name")} data-testid="input-name" />
            <input className={inputCls} type="email" placeholder="Email address *" value={form.email} onChange={update("email")} data-testid="input-email" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <input className={inputCls} placeholder="Phone number" value={form.phone} onChange={update("phone")} data-testid="input-phone" />
            <select className={`${inputCls} appearance-none`} value={form.service} onChange={update("service")} data-testid="input-service">
              <option value="" className="bg-ink">Service of interest</option>
              {SERVICES.map((s) => (
                <option key={s} value={s} className="bg-ink">
                  {s}
                </option>
              ))}
            </select>
          </div>
          <textarea
            className={`${inputCls} resize-none`}
            rows={4}
            placeholder="Tell us about your project *"
            value={form.message}
            onChange={update("message")}
            data-testid="input-message"
          />
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-3 bg-flame hover:bg-[#E64500] disabled:opacity-60 text-ink font-semibold px-8 py-4 transition-colors duration-300"
            data-testid="submit-inquiry-button"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowUpRight className="h-5 w-5" />}
            {loading ? "Sending..." : "Send inquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
